import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/auth'
import { api } from '../../lib/api'
import type { Notification } from '@shared/types'

interface TopbarProps {
  onCmdOpen: () => void
}

export function Topbar({ onCmdOpen }: TopbarProps) {
  const { user, logout } = useAuthStore()
  const [showNotifs, setShowNotifs] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const notifRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  const initials = user?.full_name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? 'U'

  const loadNotifications = useCallback(async () => {
    try {
      const res = await api.get<{ notifications: Notification[]; unread_count: number }>('/notifications')
      setNotifications(res.notifications)
      setUnreadCount(res.unread_count)
    } catch { /* handled */ }
  }, [])

  useEffect(() => { loadNotifications() }, [loadNotifications])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifs(false)
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function markAllRead() {
    try {
      await api.post('/notifications/read-all')
      setUnreadCount(0)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })))
    } catch { /* handled */ }
  }

  return (
    <header style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px', height: 52, flexShrink: 0,
      background: 'var(--bg-1)', borderBottom: '1px solid var(--border)',
    }}>
      {/* Left — Breadcrumb / page title */}
      <Breadcrumb pathname={location.pathname} />

      {/* Right — Search + Bell + Avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Search trigger */}
        <div
          onClick={onCmdOpen}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 14px', borderRadius: 'var(--r-m)',
            border: '1px solid var(--border)', background: 'rgba(255,255,255,.01)',
            cursor: 'pointer', color: 'var(--text-3)', fontSize: 13, fontFamily: 'var(--font)',
            transition: 'border-color var(--dur-f)',
          }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <span>Search...</span>
          <kbd style={{
            fontSize: 10, fontFamily: 'var(--mono)', padding: '1px 5px',
            borderRadius: 4, background: 'var(--bg-3)', marginLeft: 20,
          }}>⌘K</kbd>
        </div>

        {/* Notification bell */}
        <div style={{ position: 'relative' }} ref={notifRef}>
          <button
            onClick={() => { setShowNotifs(!showNotifs); setShowMenu(false) }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 6, borderRadius: 'var(--r-s)', display: 'flex', alignItems: 'center',
              color: 'var(--text-2)', transition: 'background var(--dur-f)',
              position: 'relative',
            }}
            title="Notifications"
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4,
                width: 16, height: 16, borderRadius: '50%',
                background: 'var(--err)', color: '#fff',
                fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--bg-1)',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifs && (
            <div className="notif-dropdown">
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderBottom: '1px solid var(--border)',
              }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{
                    fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer',
                  }}>
                    Mark all read
                  </button>
                )}
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {notifications.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-3)', textAlign: 'center', padding: '24px 0' }}>No notifications</p>
                ) : notifications.slice(0, 20).map((n) => (
                  <div key={n.id} style={{
                    padding: '10px 16px', borderBottom: '1px solid var(--border)',
                    background: !n.is_read ? 'var(--accent-m)' : 'transparent',
                  }}>
                    <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{n.title}</p>
                    {n.body && <p style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{n.body}</p>}
                    <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4, fontFamily: 'var(--mono)' }}>
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* User avatar */}
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={() => { setShowMenu(!showMenu); setShowNotifs(false) }}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 'var(--r-f)',
              background: 'linear-gradient(135deg, var(--accent), var(--accent-h))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: 'var(--accent-t)', fontFamily: 'var(--mono)',
            }}>
              {initials}
            </div>
          </button>
          {showMenu && (
            <div className="user-dropdown">
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{user?.full_name}</p>
                <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{user?.email}</p>
              </div>
              <button
                onClick={() => { logout(); navigate('/login') }}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 16px',
                  fontSize: 13, color: 'var(--err)', background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/clients': 'Clients',
  '/sites': 'Sites',
  '/proposals': 'Proposals',
  '/dispatch': 'Dispatch',
  '/tickets': 'Tickets',
  '/kb': 'Knowledge Base',
  '/network': 'Network',
  '/products': 'Products',
  '/pipeline': 'Pipeline',
  '/invoices': 'Invoices',
  '/contracts': 'Contracts',
  '/purchase-orders': 'Purchase Orders',
  '/commissions': 'Commissions',
  '/deals': 'Deals',
  '/time': 'Time Tracking',
  '/tasks': 'Tasks',
  '/assets': 'Assets',
  '/settings': 'Settings',
  '/platform': 'Platform',
  '/floor-plans': 'Floor Plans',
  '/projects': 'Projects',
  '/onboarding': 'Onboarding',
  '/chat': 'Chat',
}

function Breadcrumb({ pathname }: { pathname: string }) {
  const segments = pathname.split('/').filter(Boolean)
  const section = segments[0] || ''
  const title = ROUTE_LABELS['/' + section] || ROUTE_LABELS[pathname] || ''

  return <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>{title}</div>
}
